/**
 * Profile walk-through (Oct 2026): a step-by-step pop-up that asks for the HR essentials —
 * Address → Children → Emergency contact → Bank details → Signature. Mounted once in App.tsx:
 * it auto-opens on Home (at most once a day while something mandatory is missing) when the
 * rollout switch (`config.profileOnboarding`: off / admins / all) includes the person, and it
 * opens on demand from Home's "Complete your profile" row or the profile page via
 * `openProfileOnboarding()`. Auto-open happens on whatever page the person lands on (9 Oct: not only Home),
 * except signing pages and the split view. Field values are saved through the normal self-service callables;
 * progress/skips go through `profileOnboardingMark`. Never shown while impersonating.
 *
 * Mandatory profile (26 Oct 2026): while a mandatory step is open it is a HARD GATE — it opens on every page
 * (signing pages and the split view excepted), has no close button and no "Later"; Sign out is the only way
 * out. Optional steps (signature, children outside Switzerland) can still be skipped.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import {
  emergencyContactsComplete,
  isIsoDate,
  normalizePhoneE164,
  CALLABLES,
  profileOnboardingBlocks,
  profileOnboardingEnabledFor,
  profileOnboardingNeedsReview,
  profileOnboardingShouldAutoOpen,
  profileOnboardingStatus,
  type Child,
  type EmergencyContact,
  type ProfileOnboardingStepKey,
  type SelfUpdateProfileInput,
  type SelfUpdateProfileResponse,
} from '@daaily/shared';
import { useProfile } from '../profile/ProfileProvider';
import { useAuth } from '../auth/AuthProvider';
import { getImpersonation } from '../auth/impersonation';
import { isEmbedded } from '../components/SplitView';
import { Modal } from '../components/Modal';
import { ChildrenEditor, cleanChildren } from '../profile/Children';
import { EmergencyContactsEditor, cleanEmergencyContacts } from '../profile/EmergencyContacts';
import { SavedSignatureCard } from '../signatures/SavedSignatureCard';

const OPEN_EVENT = 'daaily:profile-onboarding-open';
/** Open the walk-through from anywhere (Home row, profile page). */
export function openProfileOnboarding() {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

const callSelfUpdate = httpsCallable<Partial<SelfUpdateProfileInput>, SelfUpdateProfileResponse>(functions, CALLABLES.SELF_UPDATE_PROFILE);
const callMark = httpsCallable<{ action: 'shown' | 'later' | 'skip' | 'noChildren' | 'done' | 'reviewed'; step?: ProfileOnboardingStepKey }, { ok: true }>(functions, CALLABLES.PROFILE_ONBOARDING_MARK);
const callRequestPrivateEmail = httpsCallable<{ email: string }, { pendingEmail: string }>(functions, CALLABLES.REQUEST_PRIVATE_EMAIL_CHANGE);

/** Status + visibility for the current user — shared by the modal, Home and the profile page. */
export function useProfileOnboarding() {
  const { state } = useProfile();
  const ready = state.status === 'ready';
  const enabled = ready && profileOnboardingEnabledFor(state.config, state.isAdmin, state.user) && !getImpersonation();
  const status = useMemo(() => (ready ? profileOnboardingStatus(state.user) : null), [ready, ready ? state.user : null]);
  // Review owed: everything is on file but never confirmed → "Check your details" once.
  const needsReview = !!status && ready && profileOnboardingNeedsReview(status, state.user.profileOnboarding ?? null);
  // Hard gate: a mandatory step is still open.
  const blocking = profileOnboardingBlocks(enabled, status);
  return { enabled, status, needsReview, blocking, user: ready ? state.user : null };
}

const STEP_INTRO: Record<ProfileOnboardingStepKey, string> = {
  address: 'Your postal address, used for contracts, payslips and official letters.',
  birthDate: 'Your date of birth, for payroll, insurance and contracts. Private: only HR and you can see it.',
  email: 'A private email address, so HR can reach you outside the company mailbox (for example for payslips after you leave). We send a link to confirm it.',
  phone: 'Your mobile number, with the country code. HR can reach you there, and the codes for signing contracts are sent to it by text message. Private: only HR and you can see it.',
  children: 'Children and dependents. Swiss employees must list them for family allowances and AHV; for everyone else this is optional.',
  emergency: 'Who should we call if something happens to you at work? Each contact needs a name, the relationship and a phone number.',
  bank: 'Where salary and expense reimbursements go. Changes are reported to HR.',
  signature: 'Save a signature once and sign hub documents in one click. Optional.',
};

export function ProfileOnboardingModal() {
  const { enabled, status, needsReview, blocking, user } = useProfileOnboarding();
  const { signOut } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [autoChecked, setAutoChecked] = useState(false);

  // Open on demand from anywhere.
  useEffect(() => {
    const h = () => { setIdx(0); setErr(null); setOpen(true); };
    window.addEventListener(OPEN_EVENT, h);
    return () => window.removeEventListener(OPEN_EVENT, h);
  }, []);

  // Auto-open on the first page after sign-in (not only Home), once a day, while something is open.
  // Skipped on signing pages (a document in progress) and inside the split view.
  const quiet = /^\/sign(\/|$)|^\/signatures\/sign/.test(pathname) || isEmbedded();
  useEffect(() => {
    if (autoChecked || !enabled || !status || !user || quiet) return;
    setAutoChecked(true);
    if (profileOnboardingShouldAutoOpen(status, user.profileOnboarding ?? null, Date.now())) {
      setIdx(0);
      setOpen(true);
      void callMark({ action: 'shown' }).catch(() => undefined);
    }
  }, [autoChecked, enabled, status, user, quiet]);

  if (!enabled || !status || !user) return null;
  // The gate keeps the walk-through open on every page until the mandatory steps are filled.
  const forced = blocking && !quiet;
  // Fill mode: only what is missing. Review mode (profile complete, never confirmed, or opened on demand when
  // complete): every step with the current values, "Looks right" per step, "All correct" at the end.
  const review = status.complete;
  const pending = review ? status.steps.filter((s) => !s.muted) : status.steps.filter((s) => !s.done && !s.muted);
  const step = pending[Math.min(idx, Math.max(0, pending.length - 1))] ?? null;
  const last = idx >= pending.length - 1;

  const close = async (how: 'later' | 'done' | 'reviewed') => {
    if (how === 'later' && blocking) return; // mandatory steps open: no "Later"
    setOpen(false);
    try { await callMark({ action: how }); } catch { /* best effort */ }
  };
  const next = async () => {
    if (review) { if (last) await close('reviewed'); else setIdx((i) => i + 1); return; }
    // Fill mode re-evaluates from the live user doc: once the save lands, the step drops out of `pending`.
    if (pending.length <= 1) await close('done');
    else setIdx((i) => Math.min(i + 1, pending.length - 1));
  };
  const skip = async (k: ProfileOnboardingStepKey) => {
    setBusy(true);
    try { await callMark({ action: 'skip', step: k }); } catch { /* best effort */ } finally { setBusy(false); }
    await next();
  };

  return (
    <Modal open={open || forced} onClose={() => void close('later')} dismissible={!blocking} title={review ? 'Check your details' : 'Complete your profile'} widthClass="max-w-2xl">
      <div className="space-y-5">
        {blocking && <p className="text-sm text-fg-muted">HR needs these details from everyone. Please fill them in before using the hub; it takes a few minutes.</p>}
        {review && !needsReview && <p className="text-sm text-fg-muted">Everything HR needs is on file. Walk through it once more and change anything that is out of date.</p>}
        {review && needsReview && <p className="text-sm text-fg-muted">Everything HR needs is on file. Please check each item is still correct and confirm at the end.</p>}
        <ol className="flex flex-wrap gap-2 text-xs">
          {status.steps.filter((s) => !s.muted).map((s) => (
            <li key={s.key} className={`rounded-full px-3 py-1 border ${s.key === step?.key ? 'border-brand-solid text-fg' : s.done ? 'border-success/40 bg-success-subtle text-success' : 'border-line text-fg-muted'}`}>
              {s.done && s.key !== step?.key ? '✓ ' : ''}{s.label}{!s.mandatory && !s.done ? ' (optional)' : ''}
            </li>
          ))}
        </ol>
        {err && <p className="text-sm text-danger bg-danger-subtle border border-danger/30 rounded-md p-3">{err}</p>}
        {!step ? (
          <div className="space-y-3">
            <p className="text-sm text-fg">All done — thank you. You can change any of this on your profile at any time.</p>
            <div className="flex justify-end"><button type="button" className="btn-primary text-sm" onClick={() => void close('done')}>Close</button></div>
          </div>
        ) : (
          <>
            <p className="text-sm text-fg-muted">{STEP_INTRO[step.key]}</p>
            {step.key === 'birthDate' && <BirthDateStep key={`d-${idx}`} user={user} review={review} last={last} busy={busy} setBusy={setBusy} setErr={setErr} onSaved={next} />}
            {step.key === 'address' && <AddressStep key={`a-${idx}`} user={user} review={review} last={last} busy={busy} setBusy={setBusy} setErr={setErr} onSaved={next} />}
            {step.key === 'email' && <EmailStep key={`m-${idx}`} user={user} review={review} last={last} busy={busy} setBusy={setBusy} setErr={setErr} onSaved={next} />}
            {step.key === 'phone' && <PhoneStep key={`p-${idx}`} user={user} review={review} last={last} busy={busy} setBusy={setBusy} setErr={setErr} onSaved={next} />}
            {step.key === 'children' && <ChildrenStep key={`c-${idx}`} initial={user.children ?? []} mandatory={step.mandatory} review={review} last={last} busy={busy} setBusy={setBusy} setErr={setErr} onSaved={next} onSkip={() => skip('children')} />}
            {step.key === 'emergency' && <EmergencyStep key={`e-${idx}`} initial={user.emergencyContacts ?? []} review={review} last={last} busy={busy} setBusy={setBusy} setErr={setErr} onSaved={next} />}
            {step.key === 'bank' && <BankStep key={`b-${idx}`} user={user} review={review} last={last} busy={busy} setBusy={setBusy} setErr={setErr} onSaved={next} />}
            {step.key === 'signature' && (
              <div className="space-y-3">
                <SavedSignatureCard />
                <div className="flex justify-between">
                  {!review && <button type="button" className="text-sm text-fg-muted hover:underline" onClick={() => void skip('signature')} disabled={busy}>Skip for now</button>}
                  <button type="button" className="btn-primary text-sm ml-auto" onClick={() => void next()} disabled={busy}>
                    {review ? (last ? 'All correct' : 'Looks right, next') : user.savedSignature ? 'Continue' : 'Done without a signature'}
                  </button>
                </div>
              </div>
            )}
            {blocking ? (
              <p className="text-xs text-fg-faint">
                Needed before you can continue. <button type="button" className="hover:underline" onClick={() => void signOut()}>Sign out</button>
              </p>
            ) : (
              <p className="text-xs text-fg-faint">
                <button type="button" className="hover:underline" onClick={() => void close('later')}>Later</button>{needsReview || !review ? ' · we will ask again tomorrow.' : ''}
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

type StepProps = { busy: boolean; setBusy: (b: boolean) => void; setErr: (e: string | null) => void; onSaved: () => Promise<void>; review?: boolean; last?: boolean };

/** Review mode: confirm as-is, or save when something was changed. */
function ReviewButtons({ changed, busy, last, onSave, onConfirm, saveDisabled }: { changed: boolean; busy: boolean; last?: boolean; onSave: () => void; onConfirm: () => void; saveDisabled?: boolean }) {
  return changed ? (
    <button type="button" className="btn-primary text-sm" disabled={saveDisabled || busy} onClick={onSave}>{busy ? 'Saving…' : last ? 'Save and finish' : 'Save and next'}</button>
  ) : (
    <button type="button" className="btn-primary text-sm" disabled={busy} onClick={onConfirm}>{last ? 'All correct' : 'Looks right, next'}</button>
  );
}

function Field({ label, value, onChange, placeholder, maxLength }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; maxLength?: number }) {
  return (
    <label className="block">
      <span className="block text-xs text-fg-faint mb-1">{label}</span>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} maxLength={maxLength} className="w-full" />
    </label>
  );
}

function BirthDateStep({ user, busy, setBusy, setErr, onSaved, review, last }: StepProps & { user: { dateOfBirth?: string | null } }) {
  const onFile = user.dateOfBirth ?? '';
  const [dob, setDob] = useState(onFile);
  const today = new Date().toISOString().slice(0, 10);
  const ok = isIsoDate(dob) && dob <= today && dob >= '1900-01-01';
  const save = async () => {
    setErr(null); setBusy(true);
    try { await callSelfUpdate({ dateOfBirth: dob }); await onSaved(); } catch (e) { setErr(e instanceof Error ? e.message : 'Could not save.'); } finally { setBusy(false); }
  };
  // Once on file only HR changes it (the server refuses), so review mode just shows it.
  if (onFile) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-fg">{onFile}</p>
        <p className="text-xs text-fg-faint">Wrong? Ask HR to correct it.</p>
        <div className="flex justify-end">
          <button type="button" className="btn-primary text-sm" disabled={busy} onClick={() => void onSaved()}>{review ? (last ? 'All correct' : 'Looks right, next') : 'Continue'}</button>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <label className="block">
        <span className="block text-xs text-fg-faint mb-1">Date of birth</span>
        <input type="date" value={dob} max={today} min="1900-01-01" onChange={(e) => setDob(e.target.value)} className="w-full sm:w-60" />
      </label>
      <div className="flex justify-end">
        <button type="button" className="btn-primary text-sm" disabled={!ok || busy} onClick={() => void save()}>{busy ? 'Saving…' : 'Save and continue'}</button>
      </div>
    </div>
  );
}

function AddressStep({ user, busy, setBusy, setErr, onSaved, review, last }: StepProps & { user: { address?: string | null; zipCode?: string | null; city?: string | null; country?: string | null } }) {
  const init = { address: user.address ?? '', zipCode: user.zipCode ?? '', city: user.city ?? '', country: user.country ?? '' };
  const [f, setF] = useState(init);
  const changed = JSON.stringify(f) !== JSON.stringify(init);
  const ok = f.address.trim() && f.zipCode.trim() && f.city.trim() && f.country.trim().length === 2;
  const save = async () => {
    setErr(null); setBusy(true);
    try {
      await callSelfUpdate({ address: f.address.trim(), zipCode: f.zipCode.trim(), city: f.city.trim(), country: f.country.trim().toUpperCase() });
      await onSaved();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not save.'); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2"><Field label="Street and number" value={f.address} onChange={(v) => setF({ ...f, address: v })} maxLength={300} /></div>
        <Field label="Postcode" value={f.zipCode} onChange={(v) => setF({ ...f, zipCode: v })} maxLength={20} />
        <Field label="City" value={f.city} onChange={(v) => setF({ ...f, city: v })} maxLength={120} />
        <Field label="Country (2 letters, e.g. CH)" value={f.country} onChange={(v) => setF({ ...f, country: v })} maxLength={2} />
      </div>
      <div className="flex justify-end">
        {review ? <ReviewButtons changed={changed} busy={busy} last={last} saveDisabled={!ok} onSave={() => void save()} onConfirm={() => void onSaved()} />
          : <button type="button" className="btn-primary text-sm" disabled={!ok || busy} onClick={() => void save()}>{busy ? 'Saving…' : 'Save and continue'}</button>}
      </div>
    </div>
  );
}

function EmailStep({ user, busy, setBusy, setErr, onSaved, review, last }: StepProps & { user: { privateEmail?: string | null; privateEmailPending?: { email: string } | null } }) {
  const verified = user.privateEmail ?? '';
  const pending = user.privateEmailPending?.email ?? null;
  const [email, setEmail] = useState(verified);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const changed = email.trim().toLowerCase() !== verified.toLowerCase();
  const ok = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const send = async () => {
    setErr(null); setBusy(true);
    try {
      const res = await callRequestPrivateEmail({ email: email.trim() });
      setSentTo(res.data.pendingEmail);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not send the verification link.'); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <Field label="Private email" value={email} onChange={setEmail} placeholder="you@example.com" maxLength={200} />
      {verified && !changed && <p className="text-xs text-success">Verified: {verified}</p>}
      {(sentTo || (pending && !sentTo && !changed)) && (
        <p className="text-xs text-fg-muted">A verification link was sent to <span className="text-fg">{sentTo ?? pending}</span>. The address counts once you click it; you can continue in the meantime.</p>
      )}
      <div className="flex justify-end gap-2">
        {changed && ok && <button type="button" className="btn-primary text-sm" disabled={busy} onClick={() => void send()}>{busy ? 'Sending…' : 'Send verification link'}</button>}
        {(!changed || sentTo) && (
          <button type="button" className={verified ? 'btn-primary text-sm' : 'btn-secondary text-sm !py-1.5 !font-normal'} disabled={busy} onClick={() => void onSaved()}>
            {review ? (last ? 'All correct' : 'Looks right, next') : verified ? 'Continue' : 'Continue, I will confirm later'}
          </button>
        )}
      </div>
    </div>
  );
}

function PhoneStep({ user, busy, setBusy, setErr, onSaved, review, last }: StepProps & { user: { privatePhone?: string | null } }) {
  const init = user.privatePhone ?? '';
  const [phone, setPhone] = useState(init);
  const changed = phone.trim() !== init;
  const ok = !!normalizePhoneE164(phone);
  const save = async () => {
    setErr(null); setBusy(true);
    try { await callSelfUpdate({ privatePhone: phone.trim() }); await onSaved(); } catch (e) { setErr(e instanceof Error ? e.message : 'Could not save.'); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <Field label="Mobile number (with country code)" value={phone} onChange={setPhone} placeholder="+41 79 123 45 67" maxLength={40} />
      {phone.trim() && !ok && <p className="text-xs text-danger">Please start with the country code, e.g. +41 79 123 45 67 or +49 151 2345 6789.</p>}
      <div className="flex justify-end">
        {review ? <ReviewButtons changed={changed} busy={busy} last={last} saveDisabled={!ok} onSave={() => void save()} onConfirm={() => void onSaved()} />
          : <button type="button" className="btn-primary text-sm" disabled={!ok || busy} onClick={() => void save()}>{busy ? 'Saving…' : 'Save and continue'}</button>}
      </div>
    </div>
  );
}

function ChildrenStep({ initial, mandatory, busy, setBusy, setErr, onSaved, onSkip, review, last }: StepProps & { initial: Child[]; mandatory: boolean; onSkip: () => void }) {
  const [list, setList] = useState<Child[]>(initial.length ? initial.map((c) => ({ ...c })) : []);
  const cleaned = cleanChildren(list);
  const changed = JSON.stringify(cleaned) !== JSON.stringify(cleanChildren(initial));
  const save = async () => {
    setErr(null); setBusy(true);
    try { await callSelfUpdate({ children: cleaned }); await onSaved(); } catch (e) { setErr(e instanceof Error ? e.message : 'Could not save.'); } finally { setBusy(false); }
  };
  const none = async () => {
    setErr(null); setBusy(true);
    try { await callMark({ action: 'noChildren' }); await onSaved(); } catch (e) { setErr(e instanceof Error ? e.message : 'Could not save.'); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <ChildrenEditor value={list} onChange={setList} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-3 text-sm">
          {!(review && initial.length > 0) && <button type="button" className="text-fg-muted hover:underline" onClick={() => void none()} disabled={busy}>I have no children</button>}
          {!mandatory && !review && <button type="button" className="text-fg-muted hover:underline" onClick={onSkip} disabled={busy}>Skip for now</button>}
        </div>
        {review ? <ReviewButtons changed={changed} busy={busy} last={last} saveDisabled={cleaned.length === 0} onSave={() => void save()} onConfirm={() => void onSaved()} />
          : <button type="button" className="btn-primary text-sm" disabled={cleaned.length === 0 || busy} onClick={() => void save()}>{busy ? 'Saving…' : 'Save and continue'}</button>}
      </div>
    </div>
  );
}

function EmergencyStep({ initial, busy, setBusy, setErr, onSaved, review, last }: StepProps & { initial: EmergencyContact[] }) {
  const [list, setList] = useState<EmergencyContact[]>(initial.length ? initial.map((c) => ({ ...c })) : [{ name: '', relationship: '', phone: '', email: null }]);
  const cleaned = cleanEmergencyContacts(list);
  const changed = JSON.stringify(cleaned) !== JSON.stringify(cleanEmergencyContacts(initial));
  // Complete = at least one contact, each with name, relationship and phone (26 Oct 2026).
  const ok = emergencyContactsComplete(cleaned);
  const save = async () => {
    setErr(null); setBusy(true);
    try { await callSelfUpdate({ emergencyContacts: cleaned }); await onSaved(); } catch (e) { setErr(e instanceof Error ? e.message : 'Could not save.'); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <EmergencyContactsEditor value={list} onChange={setList} />
      {cleaned.length > 0 && !ok && <p className="text-xs text-danger">Every contact needs a phone number.</p>}
      <div className="flex justify-end">
        {review ? <ReviewButtons changed={changed} busy={busy} last={last} saveDisabled={!ok} onSave={() => void save()} onConfirm={() => void onSaved()} />
          : <button type="button" className="btn-primary text-sm" disabled={!ok || busy} onClick={() => void save()}>{busy ? 'Saving…' : 'Save and continue'}</button>}
      </div>
    </div>
  );
}

function BankStep({ user, busy, setBusy, setErr, onSaved, review, last }: StepProps & { user: { accountOwnerName?: string | null; iban?: string | null; bicSwift?: string | null; bankName?: string | null; displayName?: string | null } }) {
  const init = { accountOwnerName: user.accountOwnerName ?? user.displayName ?? '', iban: user.iban ?? '', bicSwift: user.bicSwift ?? '', bankName: user.bankName ?? '' };
  const [f, setF] = useState(init);
  const changed = JSON.stringify(f) !== JSON.stringify(init);
  const ok = f.accountOwnerName.trim() && f.iban.replace(/\s/g, '').length >= 12 && f.bicSwift.trim() && f.bankName.trim();
  const save = async () => {
    setErr(null); setBusy(true);
    try {
      await callSelfUpdate({ accountOwnerName: f.accountOwnerName.trim(), iban: f.iban.replace(/\s/g, '').toUpperCase(), bicSwift: f.bicSwift.trim(), bankName: f.bankName.trim() });
      await onSaved();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not save.'); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Account owner" value={f.accountOwnerName} onChange={(v) => setF({ ...f, accountOwnerName: v })} maxLength={200} />
        <Field label="IBAN" value={f.iban} onChange={(v) => setF({ ...f, iban: v })} maxLength={40} />
        <Field label="BIC / SWIFT" value={f.bicSwift} onChange={(v) => setF({ ...f, bicSwift: v })} maxLength={20} />
        <Field label="Bank name" value={f.bankName} onChange={(v) => setF({ ...f, bankName: v })} maxLength={200} />
      </div>
      <div className="flex justify-end">
        {review ? <ReviewButtons changed={changed} busy={busy} last={last} saveDisabled={!ok} onSave={() => void save()} onConfirm={() => void onSaved()} />
          : <button type="button" className="btn-primary text-sm" disabled={!ok || busy} onClick={() => void save()}>{busy ? 'Saving…' : 'Save and continue'}</button>}
      </div>
    </div>
  );
}
